//Type 1

import React, { useState, useEffect } from "react";
import Styles from "./Fileupload.module.css";
import { ReactComponent as UploadFile } from "../../../assets/images/uploadIcon.svg";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import { getDocumentsDetails } from "../../../Services/BuySell/Listing";
import { useLocation } from "react-router-dom";
import { uploadAuthDocument } from "../../../Services/ApiCalling/Auth";
import { decryptData } from "../../Decode";

// Singleton worker — created once at module load so XLSX is pre-initialized
// in the background before the user ever picks a file. Survives component
// remounts (e.g. when excelResetKey changes), so no re-initialization cost.
let _xlsxWorker = null;
let _msgId = 0;
const _pending = new Map();

function getExcelWorker() {
  if (!_xlsxWorker) {
    _xlsxWorker = new Worker(new URL("./xlsxWorker.js", import.meta.url));
    _xlsxWorker.onmessage = ({ data }) => {
      const cb = _pending.get(data.id);
      if (cb) {
        _pending.delete(data.id);
        data.error ? cb.reject(new Error(data.error)) : cb.resolve(data.sheetData);
      }
    };
    _xlsxWorker.onerror = () => {
      _pending.forEach((cb) => cb.reject(new Error("Worker error")));
      _pending.clear();
      _xlsxWorker = null;
    };
  }
  return _xlsxWorker;
}

function parseExcelInWorker(buffer) {
  return new Promise((resolve, reject) => {
    const id = ++_msgId;
    _pending.set(id, { resolve, reject });
    getExcelWorker().postMessage({ id, buffer }, [buffer]);
  });
}

// Pre-warm: spin up the worker so XLSX loads in the background while the
// user is still navigating the UI — zero initialization delay at upload time.
getExcelWorker();

const Fileupload = ({
  heading,
  value,
  onChange,
  height,
  example,
  type,
  fileType,
  error,
  field,
  id,
  accept,
  disabled,
  viewType,
  isInPlaceBid,
  viewMode,
  viewHeading,
  previewTitle,
  documentType,
  parentEntityCompanyId,
}) => {
  const location = useLocation();
  const token = useSelector((state) => state?.persistedReducer?.token);
  const [loading, setLoading] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [termsDocumentId, setTermsDocumentId] = useState(null);
  const [fileName, setFileName] = useState("");

  const isInCompanyAdmin =
    location.pathname.includes("/admin_company_dashboard") ||
    location.pathname.includes("company_admin") ||
    location.pathname.includes("companyAdmin") ||
    location.pathname.includes("/admin_dashboard_company") ||
    location.pathname.includes("/register");

  useEffect(() => {
    async function fetchDocumentDetails() {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        typeof value !== "object"
      ) {
        const aa = await getDocumentsDetails(value);
        setFileName(aa?.data?.filename);
      }
    }
    fetchDocumentDetails();
  }, [value]);

  useEffect(() => {
    if (value && typeof value === "object" && value.id) {
      setTermsDocumentId(value.id);
      setShowCheck(true);
    } else if (typeof value === "string" || typeof value === "number") {
      setTermsDocumentId(value);
      setShowCheck(true);
    }
  }, [value]);

  const handleUploadFile = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.error("No file selected for upload.");
      return;
    }

    const selectedFile = files[0];

    if (isInCompanyAdmin || location.pathname.includes("/register")) {
      if (
        selectedFile.type !== "application/pdf" &&
        !selectedFile.name.toLowerCase().endsWith(".pdf")
      ) {
        toast.error("Only PDF files are allowed for document uploads.");
        return;
      }
    }

    if (accept && selectedFile) {
      const acceptedTypes = accept
        .split(",")
        .map((type) => type.trim().toLowerCase());
      const fileName = selectedFile.name.toLowerCase();
      const fileType = selectedFile.type.toLowerCase();

      const isAccepted = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return fileName.endsWith(type);
        }
        return fileType === type;
      });

      if (!isAccepted) {
        toast.error("Selected file type is not allowed.");
        return;
      }
    }

    if (fileType === "excel") {
      const reader = new FileReader();
      setLoading(true);
      reader.onload = async (e) => {
        try {
          // Warm up the worker eagerly so XLSX is already loaded when we call this
          getExcelWorker();
          const sheetData = await parseExcelInWorker(e.target.result);
          onChange(sheetData, selectedFile.name);
        } catch (err) {
          console.error("Excel parse error:", err);
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => setLoading(false);
      reader.readAsArrayBuffer(selectedFile);
      return;
    }

    if (isInPlaceBid) {
      const form = new FormData();
      form.append("file", selectedFile);
      const url = `${process.env.REACT_APP_API_URL}/doc/upload/public/random/BSB?documentType=${documentType}&companyId=${parentEntityCompanyId}`;

      try {
        setLoading(true);
        const response = await axios.post(url, form, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `${token}`,
          },
        });
        const resData = await decryptData(response?.data);
        if (resData?.code === 200) {
          onChange({
            field: field,
            id: resData?.data?.id,
            fileName: selectedFile?.name,
          });
          setFileName(selectedFile?.name);
          setTermsDocumentId(resData?.data?.id);
          toast.success("File uploaded successfully");
          setShowCheck(true);
        } else {
          setShowCheck(false);
          console.error(
            "File upload failed with response code:",
            resData?.code,
          );
        }
      } catch (error) {
        toast.error("Error during file upload");
        console.error("Error during file upload:", error);
      } finally {
        setLoading(false);
      }

      return;
    }

    const form = new FormData();
    form.append("file", selectedFile);
    const url = `${process.env.REACT_APP_API_URL}/doc/upload/public/random/AUTH`;

    try {
      setLoading(true);
      const response = await axios.post(url, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `${token}`,
        },
      });
      const resData = await decryptData(response?.data);
      if (resData?.code === 200) {
        onChange({
          field: field,
          id: resData?.data?.id,
          fileName: selectedFile?.name,
        });
        setFileName(selectedFile?.name);
        setTermsDocumentId(resData?.data?.id);
        toast.success("File uploaded successfully");
        setShowCheck(true);
      } else {
        setShowCheck(false);
        console.error(
          "File upload failed with response code:",
          resData?.code,
        );
      }
    } catch (error) {
      toast.error("Error during file upload");
      console.error("Error during file upload:", error);
    } finally {
      setLoading(false);
    }
  };

  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();
  const displayHeading = viewMode && viewHeading ? viewHeading : heading;

  return (
    <div className={Styles.inputTextContainer}>
      <PdfPreviewModal />
      {displayHeading && (
        <div className={Styles.inputHeading}>
          {displayHeading}
          {loading && (
            <span className={Styles.loaderSpinner} style={{ marginLeft: 8 }}>
              <span />
            </span>
          )}

          {showCheck && !isInCompanyAdmin && (
            <span
              type="button"
              onClick={() => {
                handlePreviewGeneralTerms(termsDocumentId, {
                  title: previewTitle || "Terms & conditions Preview",
                });
              }}
              className={Styles.checkIcon}
            >
              <RemoveRedEyeIcon
                style={{ fontSize: "15px", color: "var(--primary-color-text)" }}
              />
            </span>
          )}
        </div>
      )}
      <div
        className={viewType === 2 ? Styles.viewType2 : Styles.inputFile}
        style={{ height: `${height ? height : 32}px` }}
      >
        <input
          disabled={disabled || loading}
          type="file"
          accept={accept}
          onChange={handleUploadFile}
          id={id}
        />
        <label
          style={{ backgroundColor: disabled ? "#e8e9e8" : "" }}
          className={viewType === 2 ? Styles.viewType2Label : Styles.inputLabel}
          htmlFor={loading ? undefined : id}
        >
          {loading ? (
            <>
              <span className={Styles.loaderSpinner} />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <UploadFile style={{ height: viewType === 2 ? "25px" : "20px" }} />
              <span>{fileName || example}</span>
            </>
          )}
        </label>
      </div>
      {error && <div className={Styles.errorHeading}>{error}</div>}
    </div>
  );
};

export default Fileupload;
